import { Duration, humanizeDuration, P, prettyBytes } from '@piccolohealth/util';
import React from 'react';
import { ChakraV3 } from '..';
import type { FileUploadProgress } from '../hooks/useFileUpload';
import { FileIcon } from './FileIcon';

const getProgressProps = (item: FileUploadProgress) => {
  const uploadingText =
    !P.isNil(item.total) && !P.isNil(item.percentage) && !P.isUndefined(item.loaded)
      ? `${P.round(item.percentage)}% - ${prettyBytes(item.loaded)} of ${prettyBytes(item.total)}`
      : '';

  const elapsed = !P.isNil(item.elapsed) ? humanizeDuration(Duration.fromMillis(item.elapsed)) : '';

  switch (item.status) {
    case 'idle': {
      return { colorPalette: 'gray', animated: false, progressText: '', elapsed };
    }
    case 'uploading': {
      return { colorPalette: 'purple', animated: true, progressText: uploadingText, elapsed };
    }
    case 'errored': {
      return { colorPalette: 'red', animated: false, progressText: item.error ?? '', elapsed };
    }
    case 'finished': {
      return {
        colorPalette: 'green',
        animated: false,
        item,
        progressText: uploadingText,
        elapsed,
      };
    }
  }
};

interface Props {
  files: FileUploadProgress[];
  showSummary?: boolean;
}

export const FileUploadItemsProgress = (props: Props) => {
  const { files, showSummary = true } = props;

  if (P.isEmpty(files)) {
    return null;
  }

  const rows = files.map((item: FileUploadProgress) => {
    const { colorPalette, animated, progressText, elapsed } = getProgressProps(item);

    return (
      <ChakraV3.HStack key={item.id} w='full'>
        <FileIcon name={item.file.name} w={12} h={12} flexShrink={0} />
        <ChakraV3.Stack gap={1} w='full' truncate>
          <ChakraV3.Text truncate fontSize='sm'>
            {item.file.name}
          </ChakraV3.Text>
          <ChakraV3.Progress.Root
            value={P.round(item.percentage)}
            rounded='xl'
            striped
            colorPalette={colorPalette}
            animated={animated}
            size='xs'
            shape='full'
          >
            <ChakraV3.Progress.Track>
              <ChakraV3.Progress.Range />
            </ChakraV3.Progress.Track>
          </ChakraV3.Progress.Root>
          <ChakraV3.HStack w='full'>
            <ChakraV3.Text fontSize='xs' color='secondary'>
              {progressText}
            </ChakraV3.Text>
            <ChakraV3.Spacer />
            <ChakraV3.Text fontSize='xs' color='secondary' flexShrink={0}>
              {elapsed}
            </ChakraV3.Text>
          </ChakraV3.HStack>
        </ChakraV3.Stack>
      </ChakraV3.HStack>
    );
  });

  const finished = files.filter((f) => f.status === 'finished' || f.status === 'errored').length;
  const total = files.length;
  const percent = P.round((finished / total) * 100);

  return (
    <ChakraV3.Stack gap={4} h='full' w='full'>
      {showSummary && (
        <ChakraV3.Text fontSize='lg' fontWeight='semibold'>
          Uploaded {finished}/{total} ({percent}%)
        </ChakraV3.Text>
      )}
      <ChakraV3.Stack w='full' gap={4}>
        {rows}
      </ChakraV3.Stack>
    </ChakraV3.Stack>
  );
};
